import { t, type MessageKey } from '@reos/i18n';
import { DomainApiError, type ValidationIssue } from './api-error.js';

/**
 * Krok UXP2 (P2-4) — SSOT pro „formuláře nemlčí": normalizace čísel se
 * zápisem po českém zvyku (mezery, NBSP) a nalezení/zaostření prvního
 * chybného pole. Žije tu, ne v každém formuláři zvlášť, aby se nerozešly.
 */

const LOCALE = 'cs';

/**
 * Krok SML5 bod 3 — LIDSKÁ HLÁŠKA PODLE DŮVODU, NE „Validace vstupu selhala".
 *
 * Server vrací u 4xx obecný titulek (`error.validation`) a konkrétní důvod
 * jen ve strojovém `type` (problem+json). Formulář, který zobrazí `message`,
 * tím pádem makléřce řekne „Validace vstupu selhala" i tam, kde je důvod
 * známý a dá se napsat, co udělat (nahrála .doc, soubor je přes limit,
 * dokument je prázdný…). Mapa `type` → klíč katalogu žije TADY, protože ji
 * potřebuje víc formulářů (import šablony, nová smlouva, klient) a dvě kopie
 * téže pravdy se vždy rozejdou.
 *
 * Neznámý `type` se NEPŘEKLÁDÁ: text ze serveru je už lokalizovaný a
 * pravdivější než náš odhad (viz `DomainApiError`).
 */
const ERROR_TEXT_BY_TYPE: Readonly<Record<string, MessageKey>> = {
  'contract-templates/unsupported-media-type': 'webAdmin.contracts.templates.uploadErrorNotDocx',
  'request/multipart-required': 'webAdmin.contracts.templates.uploadErrorNotDocx',
  'contract-templates/too-large': 'webAdmin.contracts.templates.uploadErrorTooLarge',
  'contract-templates/infected': 'webAdmin.contracts.templates.uploadErrorInfected',
  'contract-templates/file-missing': 'webAdmin.contracts.templates.uploadErrorEmpty',
  // Kody z `DocxParseError` (server je prepisuje `docx/` → `docx-`).
  'contract-templates/docx-empty': 'webAdmin.contracts.templates.uploadErrorEmpty',
  'contract-templates/docx-empty-document': 'webAdmin.contracts.templates.uploadErrorEmpty',
  'contract-templates/docx-no-document': 'webAdmin.contracts.templates.uploadErrorUnreadable',
  'contract-templates/docx-not-a-zip': 'webAdmin.contracts.templates.uploadErrorUnreadable',
  'contract-templates/docx-corrupt': 'webAdmin.contracts.templates.uploadErrorUnreadable',
  'contract-templates/docx-unsupported-compression':
    'webAdmin.contracts.templates.uploadErrorUnreadable',
  'contract-templates/docx-too-large': 'webAdmin.contracts.templates.uploadErrorTooLarge',
  'contract-templates/docx-too-many-entries': 'webAdmin.contracts.templates.uploadErrorTooLarge',
};

/**
 * Věta pro člověka z chyby API. Pro známý `type` text z katalogu, jinak
 * hlášku ze serveru a teprve pak obecnou záložní větu volajícího.
 */
/**
 * Strojový kód z `type`. Problem+json chodí z API ve DVOU tvarech:
 * plná URI (`https://docs.reos.local/errors/contract-templates/too-large`,
 * tak ji skládá `ProblemError`) i holý kód (`listing-draft/ai-unavailable`
 * u ručních klientů). Živé ověření 12. 10. 2026 nad lokálním API vrátilo
 * tvar s URI — mapa podle holého kódu by nikdy nesedla a makléřce by zůstalo
 * „Validace vstupu selhala".
 */
export function kodChyby(type: string): string {
  const index = type.lastIndexOf('/errors/');
  return index < 0 ? type : type.slice(index + '/errors/'.length);
}

/**
 * Lidská věta pro ZNÁMÝ důvod, jinak `undefined`. Formuláře, které si už
 * zálohu řeší samy (`errorMessage(err) ?? t('…loadError')`), ji jen
 * předřadí — nemusí kvůli tomu měnit svůj tok.
 */
export function humanApiErrorText(error: unknown): string | undefined {
  const key =
    error instanceof DomainApiError && error.type !== undefined
      ? ERROR_TEXT_BY_TYPE[kodChyby(error.type)]
      : undefined;
  return key === undefined ? undefined : t(key, { locale: LOCALE });
}

export function humanApiError(error: unknown, fallbackKey: MessageKey): string {
  const znamy = humanApiErrorText(error);
  if (znamy !== undefined) return znamy;
  const message = error instanceof Error ? error.message.trim() : '';
  // Obecné titulky serveru („Validace vstupu selhala", „Neplatný požadavek")
  // nejsou hláška pro člověka — tam má přednost věta formuláře.
  if (message !== '' && !GENERIC_SERVER_TITLES.some((g) => message === t(g, { locale: LOCALE }))) {
    return message;
  }
  return t(fallbackKey, { locale: LOCALE });
}

/** Titulky, které o příčině neříkají nic — formulář má vlastní větu. */
const GENERIC_SERVER_TITLES: readonly MessageKey[] = [
  'error.validation',
  'error.bad_request',
  'error.internal',
];

/**
 * Odstraní mezery (obyčejné i NBSP/úzké) z čísla psaného po skupinách
 * ("5 000 000") a desetinnou čárku převede na tečku, aby odpovídalo tvaru,
 * který čekají `moneySchema`/`areaSchema` (`^\d+(\.\d+)?$`). Vstup, který po
 * očištění není číslo, se vrátí beze změny — validace pole to ohlásí sama.
 */
export function normalizeNumberInput(raw: string): string {
  const stripped = raw.replace(/[\s\u00a0\u202f]/gu, '');
  return stripped.replace(',', '.');
}

/** Najde issue API pro dané pole — cesta chodí jako `wantedPriceTo` i `/wantedPriceTo`. */
export function issueForField(
  issues: readonly ValidationIssue[],
  field: string,
): ValidationIssue | undefined {
  return issues.find((issue) => {
    const segments = issue.path.split(/[./]/u).filter((s) => s !== '');
    return segments[segments.length - 1] === field;
  });
}

/**
 * Posune stránku k prvnímu chybnému poli a dá na něj fokus (P2-4: fokus
 * zůstával na tlačítku, pole se zvýrazněním bylo mimo obrazovku).
 */
export function focusField(testId: string): void {
  const el = document.querySelector<HTMLElement>(`[data-testid="${testId}"]`);
  if (el === null) return;
  el.scrollIntoView({ block: 'center' });
  el.focus();
}
